import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import subscriptionService from '../services/subscriptionService';
import { PayPalConsent } from './PayPalConsent';
import {
  Crown,
  Check,
  Zap,
  Star,
  Loader2,
  X,
  Sparkles,
  Shield,
  TrendingUp
} from 'lucide-react';

const SubscriptionPlans = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansData, statusData] = await Promise.all([
        subscriptionService.getPlans(),
        subscriptionService.getStatus(token)
      ]);
      
      setPlans(plansData.plans);
      setCurrentPlan(statusData);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const [paypalOn, setPaypalOn] = useState(false);
  const [paypalConsent, setPaypalConsent] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setPaypalOn(!!d.configured)).catch(() => {});
  }, [token]);
  const handlePayPal = async (planId) => {
    setProcessing(`pp-${planId}`);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/subscribe`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ plan: planId, recurring_consent: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      window.location.href = d.approve_url;
    } catch (e) { toast.error(e.message || 'No se pudo iniciar PayPal'); setProcessing(null); }
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free' || planId === currentPlan?.plan) return;
    
    setProcessing(planId);
    try {
      const { checkout_url } = await subscriptionService.createCheckout(token, planId);
      
      // Redirect to Stripe checkout
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Error al procesar el pago');
    } finally {
      setProcessing(null);
    }
  };

  const formatPrice = (priceCLP) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(priceCLP);
  };

  if (loading) {
    return (
      <div data-testid="subscription-plans-dialog" role="dialog" aria-modal="true" aria-label="Planes de suscripción" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-2xl p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-4 text-slate-400">Cargando planes...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="subscription-plans-dialog" role="dialog" aria-modal="true" aria-label="Planes de suscripción" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold font-['Outfit']">Planes NotaryOS</h2>
              <p className="text-slate-400">Elige el plan que mejor se adapte a tus necesidades</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Plan Badge */}
        {currentPlan?.is_premium && (
          <div className="mx-6 mt-4 p-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-400">
              Actualmente tienes el plan {currentPlan.plan === 'premium_monthly' ? 'Premium Mensual' : 'Premium Anual'}
            </span>
          </div>
        )}

        {/* Plans Grid */}
        <div className="p-6 grid md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlan?.plan;
            const isPremium = plan.id !== 'free';
            const isHighlighted = plan.highlight;

            return (
              <div
                key={plan.id}
                className={`relative rounded-xl border p-6 transition-all ${
                  isHighlighted
                    ? 'border-blue-500 bg-blue-500/5 scale-105 shadow-lg shadow-blue-500/20'
                    : 'border-border bg-slate-900/50 hover:border-slate-700'
                }`}
              >
                {/* Popular Badge */}
                {isHighlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Más Popular
                    </span>
                  </div>
                )}

                {/* Current Badge */}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Tu Plan
                    </span>
                  </div>
                )}

                {/* Plan Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  isPremium 
                    ? 'bg-gradient-to-br from-yellow-500 to-orange-500' 
                    : 'bg-slate-800'
                }`}>
                  {isPremium ? (
                    <Crown className="w-6 h-6 text-white" />
                  ) : (
                    <Shield className="w-6 h-6 text-slate-400" />
                  )}
                </div>

                {/* Plan Name */}
                <h3 className="text-xl font-bold font-['Outfit'] mb-1">{plan.name}</h3>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-3xl font-bold">
                    {plan.price_clp === 0 ? 'Gratis' : formatPrice(plan.price_clp)}
                  </span>
                  {plan.price_clp > 0 && (
                    <span className="text-slate-400 text-sm">
                      /{plan.period === 'monthly' ? 'mes' : 'año'}
                    </span>
                  )}
                </div>

                {/* Savings Badge */}
                {plan.savings && (
                  <div className="mb-4 inline-flex items-center gap-1 text-emerald-400 text-sm bg-emerald-400/10 px-2 py-1 rounded-full">
                    <TrendingUp className="w-4 h-4" />
                    {plan.savings}
                  </div>
                )}

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        feature.includes('ILIMITADO') || feature.includes('ilimitado')
                          ? 'text-yellow-400'
                          : 'text-emerald-400'
                      }`} />
                      <span className={
                        feature.includes('ILIMITADO') || feature.includes('ilimitado')
                          ? 'text-yellow-200 font-medium'
                          : 'text-slate-300'
                      }>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={plan.id === 'free' || isCurrent || processing}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    isCurrent
                      ? 'bg-slate-800 text-slate-400 cursor-not-allowed'
                      : plan.id === 'free'
                      ? 'bg-slate-800 text-slate-400 cursor-default'
                      : isHighlighted
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
                      : 'bg-slate-800 hover:bg-slate-700 text-white'
                  }`}
                >
                  {processing === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Procesando...
                    </>
                  ) : isCurrent ? (
                    'Plan Actual'
                  ) : plan.id === 'free' ? (
                    'Plan Base'
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Suscribirse
                    </>
                  )}
                </button>
                {plan.id === 'premium_monthly' && !isCurrent && (
                  <button
                    data-testid={`paypal-subscribe-${plan.id}`}
                    onClick={() => setPaypalConsent(true)}
                    disabled={!!processing || !paypalOn}
                    className="w-full mt-2 py-2.5 px-4 rounded-lg font-bold bg-[#ffc439] hover:bg-[#f2b92f] text-slate-900 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {processing === `pp-${plan.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : paypalOn ? 'Suscribirse con PayPal' : 'PayPal · conexión pendiente'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-slate-900/30 rounded-b-2xl">
          <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Pago seguro con Stripe
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Cancela cuando quieras
            </div>
          </div>
        </div>
      </div>
      <PayPalConsent open={paypalConsent} onClose={() => setPaypalConsent(false)} onConfirm={() => handlePayPal('premium_monthly')} busy={!!processing} />
    </div>
  );
};

export default SubscriptionPlans;
